import { format } from 'date-fns';
import { UserService } from 'src/services/api/UserService';

// Some senior_level_flow actions need more than "which candidates" — e.g.
// assign_slot needs the actual interview date/time and panel. This is
// the sole source of truth for which actions require a payload-collection
// drawer before firing (see GenericFlowAction.jsx) and how to shape each
// field's raw form value into the shape the API expects.
//
// Payload contract:
//   POST /internal/assignments/candidates-pipeline/88/flow-actions/assign_slot
//   { "payload": { "interview_datetime": "2026-09-10T14:00:00+05:30",
//                   "interviewers": [123, 456], "mode": "Online" } }
// `mode` has no UI field anymore — per explicit request, every interview
// this platform books is Online, so buildInterviewSlotPayload below always
// sends "Online" rather than asking the user to pick it.
//
// Timezone: interview_datetime is sent as a real ISO 8601 string with an
// explicit "+05:30" offset — self-describing, no client-side UTC math
// needed, backend does the interpretation. NOTE this reverses an earlier
// version of this contract, which appended no offset at all ("backend no
// longer wants the IST offset appended", confirmed live in an earlier
// session) — that made the payload ambiguous (just naive digits, easy to
// misread as UTC on the backend side and land 5:30 off). Re-added per
// explicit request; not yet re-verified live against the current dev
// backend that it still accepts an offset — flag if a real call rejects it.
//
// interview_datetime: field type 'date-time' renders the common
// src/components/common/DateTimePicker.jsx (Calendar popover + native time
// input) instead of a plain <input type="datetime-local">, so the raw form
// value is a composite { date: Date, time: 'HH:mm' } rather than a single
// string — buildInterviewSlotPayload below combines the two as IST
// wall-clock (this platform's users are all IST) and appends the fixed
// +05:30 offset.
//
// mode: always "Online" now (see the field removal above) — "Offline" was
// the natural counterpart this form used to also offer, but the platform
// only books online interviews per explicit request, so the field (and the
// choice) is gone rather than just defaulted.
//
// reschedule_slot shares this same field set/payload shape (per explicit
// confirmation) — it's the same underlying interview-slot data, just fired
// once the candidate is already past New/Contacted.
//
// Interviewer(s): AsyncMultiSelectDropdown (src/components/common/) against
// GET /internal/admin/ahedge-users, filtered server-side to role=
// ahedge_interviewer — the same role value the Ahedge Users admin page's
// own role filter already offers (AhedgeUsers.jsx's roleOptions), confirmed
// live to be a real, already-populated role rather than a guess. Was
// fetching role='all' before; narrowed to just interviewers per explicit
// request, since not every ahedge staff member should be pickable as a
// candidate's interviewer. Confirmed live this does shrink the visible
// list in the current dev dataset — most seeded staff users have no role
// set at all (shows as "—" on the admin page), so only accounts actually
// tagged Interviewer there will appear here; that's the intended behavior,
// not a bug, but worth knowing if the dropdown looks sparse against this
// same dataset later.
//
// That endpoint has no `search` query param today (its own endpoint
// template only takes role/page/pageSize), so real server-side
// search-as-you-type isn't possible yet. Fetched once per role — a
// generous single page, reasonable for an internal staff list rather than
// a customer-scale one — and cached, then paginated/filtered by name or
// email client-side so the dropdown's own search box still does something
// real instead of nothing. Swap fetchAhedgeUsers/interviewerFetcher for a
// real server-side call the moment the endpoint gains `search`.
let ahedgeUsersCache = null;
function fetchAhedgeUsers() {
  if (!ahedgeUsersCache) {
    // 100 is this endpoint's own confirmed max page size (a 200 request came
    // back a 422 — "pageSize should be less than or equal to 100").
    ahedgeUsersCache = UserService.getAhedgeUsers(
      'ahedge_interviewer',
      1,
      100
    ).then((res) => res?.data?.users || []);
  }
  return ahedgeUsersCache;
}
async function interviewerFetcher({ page, pageSize, search }) {
  const all = await fetchAhedgeUsers();
  const q = (search || '').trim().toLowerCase();
  const filtered = q
    ? all.filter((u) =>
        `${u.first_name || ''} ${u.last_name || ''} ${u.email || ''}`
          .toLowerCase()
          .includes(q)
      )
    : all;
  const start = (page - 1) * pageSize;
  return {
    items: filtered.slice(start, start + pageSize),
    hasNext: start + pageSize < filtered.length,
  };
}

const interviewSlotFields = [
  {
    name: 'interview_datetime',
    label: 'Interview date & time',
    type: 'date-time',
    required: true,
    minDate: new Date(new Date().setHours(0, 0, 0, 0)),
  },
  {
    name: 'interviewers',
    label: 'Interviewer(s)',
    type: 'async-multi-select',
    required: true,
    fetcher: interviewerFetcher,
    getOptionLabel: (u) =>
      `${u.first_name || ''} ${u.last_name || ''}`.trim() ||
      u.email ||
      'Unnamed',
    getOptionValue: (u) => u.id,
    // Avatar-initials + email sub-line (AsyncMultiSelectDropdown's opt-in
    // "picking people" treatment) — email disambiguates the ahedge-users
    // list's own near-duplicate display names (e.g. "Sanoop M" / "sanoop m")
    // that a name-only row can't.
    getOptionAvatarSeed: (u) =>
      `${u.first_name || ''} ${u.last_name || ''}`.trim() ||
      u.email ||
      String(u.id),
    getOptionSubLabel: (u) => u.email,
    placeholder: 'Search interviewers…',
  },
  {
    name: 'meeting_duration_minutes',
    label: 'Duration',
    // 'duration' — a Select with a fixed set of presets plus a "Custom…"
    // option that swaps in a manual minutes input (ActionPayloadModal.jsx's
    // renderField). Presets are stored as minutes directly so
    // buildInterviewSlotPayload below can send whichever one — preset or
    // typed — as a single number with no extra mapping.
    type: 'duration',
    required: true,
    options: [
      { value: '30', label: '30 minutes' },
      { value: '60', label: '1 hour' },
      { value: '120', label: '2 hours' },
    ],
  },
];

// mode is always "Online" — no field for it above, see this file's header
// comment.
//
// meeting_duration_minutes: the 'duration' field stores either one of the
// preset string values ('30'/'60'/'120') or the literal 'custom' sentinel
// with the typed value sitting in the companion
// `meeting_duration_minutes_custom` key (ActionPayloadModal.jsx's
// renderField writes both) — resolved here to the one number the API
// contract actually wants.
const buildInterviewSlotPayload = (values) => {
  const { date, time } = values.interview_datetime || {};
  const rawDuration = values.meeting_duration_minutes;
  const meetingDurationMinutes = Number(
    rawDuration === 'custom'
      ? values.meeting_duration_minutes_custom
      : rawDuration
  );
  return {
    interview_datetime: `${format(date, 'yyyy-MM-dd')}T${time}:00+05:30`,
    interviewers: (values.interviewers || []).map((opt) => opt.value),
    mode: 'Online',
    meeting_duration_minutes: meetingDurationMinutes,
  };
};

// IST is a fixed UTC+5:30 offset (no DST) — this whole platform is IST-only
// per the confirmed contract above. Any ISO datetime string (whatever offset
// it actually carries — GET interview-schedule confirmed live to send
// "+00:00") → the IST wall-clock { date, time } shape interview_datetime's
// field value uses. `new Date(...)` parses the source offset correctly on
// its own; only the display side needs the manual +5:30 shift, done via
// UTC-getters on the shifted instant so the browser's own local timezone
// never gets layered on top by mistake.
const IST_OFFSET_MS = (5 * 60 + 30) * 60 * 1000;
function isoStringToIstWallClock(isoString) {
  const parsed = new Date(isoString);
  const ist = new Date(parsed.getTime() + IST_OFFSET_MS);
  const pad = (n) => String(n).padStart(2, '0');
  return {
    date: new Date(ist.getUTCFullYear(), ist.getUTCMonth(), ist.getUTCDate()),
    time: `${pad(ist.getUTCHours())}:${pad(ist.getUTCMinutes())}`,
  };
}

// reschedule_slot only — prefills the form from the candidate's existing
// slot (GET /internal/assignments/candidates-pipeline/:secMappingId/
// interview-schedule) so reopening the modal to shift a time doesn't mean
// re-entering everything. assign_slot has no existing slot to prefill
// against, so it doesn't set `prefillFromSchedule` below.
//
// Maps the response into this form's own value shapes (see
// interviewSlotFields above). Confirmed live against a real populated
// response:
//   { interview_datetime: "2026-09-15T12:30:00+00:00", mode: "Online",
//     interviewers: [{ id: 6, name: "Sanoop M", email: "..." }, ...] }
// interview_datetime carries a +00:00 suffix — real UTC, not IST — so it's
// converted via isoStringToIstWallClock rather than copied verbatim. NOTE:
// an earlier version of this function copied the digits verbatim instead
// (treating 12:30:00+00:00 as if it were already 12:30 IST); that was wrong
// per the same UTC-vs-IST clarification that drove the write-side change
// above — fixed here to actually shift by +5:30 before displaying. Each
// interviewer entry already carries its own `name`/`email` — used directly
// rather than round-tripping through the cached ahedge-users list, which
// was this function's first guess before a real response existed. A bare
// id (not an object) is handled too, as a defensive fallback in case some
// other response ever sends one, resolved against that same cache.
export async function mapScheduleToFormValues(schedule) {
  if (!schedule) return {};
  const values = {};

  if (schedule.interview_datetime) {
    values.interview_datetime = isoStringToIstWallClock(
      schedule.interview_datetime
    );
  }

  if (Array.isArray(schedule.interviewers) && schedule.interviewers.length) {
    const bareIds = schedule.interviewers.filter(
      (entry) => !entry || typeof entry !== 'object'
    );
    const usersById = bareIds.length
      ? new Map((await fetchAhedgeUsers()).map((u) => [String(u.id), u]))
      : null;
    values.interviewers = schedule.interviewers.map((entry) => {
      if (entry && typeof entry === 'object') {
        return {
          label: entry.name || entry.email || `Interviewer #${entry.id}`,
          value: entry.id,
        };
      }
      const user = usersById?.get(String(entry));
      const label = user
        ? `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
          user.email ||
          `Interviewer #${entry}`
        : `Interviewer #${entry}`;
      return { label, value: entry };
    });
  }

  // No `mode` prefill — the field is gone (always sent as "Online" on
  // submit, see buildInterviewSlotPayload above), so there's nothing left
  // to populate it from.

  // meeting_duration_minutes: not yet confirmed live on a real
  // GET .../interview-schedule response (this endpoint didn't return it
  // before the field existed) — prefills defensively if the backend does
  // start sending it, matching one of the presets when possible or falling
  // back to 'custom' + the typed-minutes companion key otherwise. Flag if a
  // real reschedule_slot reopen doesn't restore this the way it does the
  // other fields.
  if (schedule.meeting_duration_minutes != null) {
    const durationField = interviewSlotFields.find(
      (f) => f.name === 'meeting_duration_minutes'
    );
    const preset = durationField?.options.find(
      (o) => Number(o.value) === Number(schedule.meeting_duration_minutes)
    );
    if (preset) {
      values.meeting_duration_minutes = preset.value;
    } else {
      values.meeting_duration_minutes = 'custom';
      values.meeting_duration_minutes_custom = String(
        schedule.meeting_duration_minutes
      );
    }
  }

  return values;
}

export const ACTION_PAYLOAD_SCHEMAS = {
  assign_slot: {
    title: 'Schedule Interview',
    description:
      'Set the interview date and time for the selected candidate(s).',
    fields: interviewSlotFields,
    buildPayload: buildInterviewSlotPayload,
  },
  reschedule_slot: {
    title: 'Reschedule Interview',
    description:
      'Set the new interview date and time for the selected candidate(s).',
    fields: interviewSlotFields,
    buildPayload: buildInterviewSlotPayload,
    prefillFromSchedule: true,
  },
};
